import { Controller, useFormContext } from 'react-hook-form';
import {
  RowLabel,
  RowLabelledCheckBox,
} from '../../../components/RowLabelledInput';

import {
  MobileExpandableCard,
  MobileExpandCardTrigger,
} from 'modules/shared-components/MobileExpandableCard';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

import Tooltip from 'modules/shared-components/tooltip';
import Checkbox from '../../../components/inputs/Checkbox';
import NumberInput from '../../../components/inputs/number-input';
import { DexCard } from '../../../components/cards';
import { FormValues } from '../../../trade/schema';
import { useToggle } from 'modules/utils/useToggle';
import { SortDropdown } from '../../../components/dropdown/sort-dropdown';
import { NotificationDex } from '../../../components/alerts/notification';
import { PERIOD_FILTERS } from '../../../research/components/HotTokens/HotTokensFilters';
import { HotTokensPeriods } from '../../../research/components/Wallets/usePageParams';
import CustomModal from '../../../components/modal/CustomModal';
import { ActivteFiltersBar, FilterTag } from '../../../components/filters';
import Button from 'modules/shared-components/button/button';

interface SafetyMeasureProps {
  fieldKey: string;
  label: string;
  tooltip: string;
  defaultMin: number | undefined;
  defaultMax: number | undefined;
  hasPeriod: boolean;
  prefix: string;
  suffix: string;
}

export const safetyMeasureConfigs = [
  {
    active: false,
    key: 'tokenAge',
    label: 'Token Age Range',
    tooltip:
      "The bot will not trade if the token's age is less than the minimum or greater than the maximum value (in minutes).",
    defaultMin: 60,
    addNewMin: 60,
    addNewMax: 1440,
    defaultMax: 1440,
    hasPeriod: false,
    prefix: '',
    suffix: 'min',
  },
  {
    active: false,
    key: 'marketCap',
    label: 'Market Cap Range',
    tooltip:
      "The bot will not trade if a token's market capitalization falls outside the specified range.",
    defaultMin: undefined,
    defaultMax: undefined,
    addNewMin: 50_000,
    addNewMax: 1_000_000,
    hasPeriod: false,
    prefix: '$',
    suffix: '',
  },
  {
    active: false,
    key: 'liquidity',
    label: 'Liquidity Range',
    tooltip:
      "The bot will not trade if a token's available liquidity is outside the specified range.",
    defaultMin: undefined,
    defaultMax: undefined,
    addNewMin: 50_000,
    addNewMax: 1_000_000,
    hasPeriod: false,
    prefix: '$',
    suffix: '',
  },
  {
    active: false,
    key: 'volume',
    label: 'Volume Range',
    tooltip:
      "The bot will not trade if a token's trading volume during the selected period is outside the specified range.",
    defaultMin: undefined,
    defaultMax: undefined,
    addNewMin: 50_000,
    addNewMax: 1_000_000,
    hasPeriod: true,
    prefix: '$',
    suffix: '',
  },
  {
    active: false,
    key: 'volatilityIndex',
    label: 'Volatility Index Range',
    tooltip:
      "The bot will not trade if a token's volatility index during the selected period is outside the specified range.",
    defaultMin: undefined,
    defaultMax: undefined,
    addNewMin: 5,
    addNewMax: 15,
    hasPeriod: true,
    prefix: '',
    suffix: '',
  },
  {
    active: false,
    key: 'smartBuyVolume',
    label: 'Smart Buy Volume Range',
    tooltip:
      'The bot will not trade if the buy volume from smart wallets during the selected period is outside the specified range.',
    defaultMin: undefined,
    defaultMax: undefined,
    hasPeriod: true,
    prefix: '$',
    suffix: '',
    addNewMin: 50_000,
    addNewMax: 1_000_000,
  },
  {
    active: false,
    key: 'smartBuyWallets',
    label: 'Smart Buy Wallets Range',
    tooltip:
      'The bot will not trade if the number of smart wallets buying during the selected period is outside the specified range.',
    defaultMin: undefined,
    defaultMax: undefined,
    defaultPeriod: HotTokensPeriods.TwentyFourHour,
    hasPeriod: true,
    prefix: '',
    suffix: '',
    addNewMin: 10,
    addNewMax: 10_000,
  },
  {
    active: false,
    key: 'smartSellVolume',
    label: 'Smart Sell Volume Range',
    tooltip:
      'The bot will not trade if the sell volume from smart wallets during the selected period is outside the specified range.',
    defaultMin: undefined,
    defaultMax: undefined,
    defaultPeriod: HotTokensPeriods.TwentyFourHour,
    hasPeriod: true,
    prefix: '$',
    suffix: '',
    addNewMin: 50_000,
    addNewMax: 1_000_000,
  },
  {
    active: false,
    key: 'smartSellWallets',
    label: 'Smart Sell Wallets Range',
    tooltip:
      'The bot will not trade if the number of smart wallets selling during the selected period is outside the specified range.',
    defaultMin: undefined,
    defaultMax: undefined,
    defaultPeriod: HotTokensPeriods.TwentyFourHour,
    hasPeriod: true,
    prefix: '',
    suffix: '',
    addNewMin: 10,
    addNewMax: 10_000,
  },
  {
    active: false,
    key: 'smartMoneyInflow',
    label: 'Smart Money Inflow Range',
    tooltip:
      'The bot will not trade if the net money inflow from smart wallets during the selected period is outside the specified range.',
    defaultMin: undefined,
    defaultMax: undefined,
    hasPeriod: true,
    prefix: '$',
    suffix: '',
    addNewMin: 50_000,
    addNewMax: 1_000_000,
  },
];

const RenderSafetyMeasure = ({
  fieldKey,
  label,
  tooltip,
  defaultMin,
  defaultMax,
  hasPeriod,
  prefix,
  suffix,
}: SafetyMeasureProps) => {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<FormValues>();

  const [isOpen, { open, close }] = useToggle();
  const rangePath = `safetyMeasures.${fieldKey}Range` as any;
  const periodPath = `safetyMeasures.${fieldKey}.period` as any;
  const minPath = `safetyMeasures.${fieldKey}.min` as any;
  const maxPath = `safetyMeasures.${fieldKey}.max` as any;

  const safetyMeasureError =
    errors?.safetyMeasures?.[fieldKey as keyof typeof errors.safetyMeasures];
  const errorCheck =
    watch(rangePath) &&
    typeof safetyMeasureError === 'object' &&
    safetyMeasureError !== null &&
    ('min' in safetyMeasureError ||
      'max' in safetyMeasureError ||
      'period' in safetyMeasureError);

  return (
    <div key={fieldKey}>
      <RowLabelledCheckBox
        label={
          <RowLabel label={label} isError={errorCheck} tooltip={tooltip} />
        }
        openBox={isOpen}
        setOpenBox={isOpen ? close : open}
        className="xxs:py-1"
      >
        <Controller
          name={rangePath}
          defaultValue={'' as any}
          control={control}
          render={({ field }) => (
            <Checkbox
              checked={!!field.value}
              onClick={field.onChange}
              onChange={() => {
                if (!field.value) {
                  setValue(minPath, defaultMin);
                  setValue(maxPath, defaultMax);
                  if (hasPeriod) {
                    setValue(periodPath, HotTokensPeriods.TwentyFourHour);
                  }

                  open();
                } else {
                  setValue(minPath, undefined);
                  setValue(maxPath, undefined);
                  close();
                }
              }}
              id={rangePath}
            />
          )}
        />
      </RowLabelledCheckBox>

      {isOpen && (
        <div className="grid grid-cols-3 items-center xxs:gap-x-2">
          <Controller
            name={minPath}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <NumberInput
                {...field}
                label="Min"
                inputSize="small"
                placeholder="Empty for 0"
                hideErrorMessage={true}
                prefix={prefix}
                suffix={suffix}
                error={error?.message}
              />
            )}
          />

          <Controller
            name={maxPath}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <NumberInput
                {...field}
                label="Max"
                inputSize="small"
                hideErrorMessage={true}
                placeholder="Empty for unlimited"
                prefix={prefix}
                suffix={suffix}
                error={error?.message}
              />
            )}
          />

          {hasPeriod && (
            <div className="xxs:space-y-1 block">
              <div className="flex">
                <Tooltip text="The period in which the range will be calculated">
                  <div className="xxs:text-dex-white-secondary xxs:text-sm">
                    Period
                  </div>
                </Tooltip>
              </div>
              <Controller
                name={periodPath}
                defaultValue={'' as any}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <div className="xxs:space-y-1">
                    <SortDropdown
                      className="h-8"
                      {...field}
                      mainLabel="Period"
                      options={PERIOD_FILTERS}
                    />
                    {error && (
                      <NotificationDex type="error" errorMessage={error}>
                        Error
                      </NotificationDex>
                    )}
                  </div>
                )}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export function SafetyMeasuresContent() {
  return (
    <div className="xxs:space-y-4 lg:space-y-2 flex grow flex-col max-h-[500px] overflow-y-auto">
      <DexCard className="grow lg:border-none lg:shadow-none " size="xs">
        <fieldset className="xxs:space-y-3 lg:space-y-1">
          {safetyMeasureConfigs.map((config) => (
            <RenderSafetyMeasure
              key={config.key}
              fieldKey={config.key}
              label={config.label}
              tooltip={config.tooltip}
              defaultMin={config.addNewMin}
              defaultMax={config.addNewMax}
              hasPeriod={config.hasPeriod}
              prefix={config.prefix}
              suffix={config.suffix}
            />
          ))}
        </fieldset>
      </DexCard>
    </div>
  );
}

export function SafetyMeasuresModal({
  handleClose,
}: {
  handleClose: () => void;
}) {
  return (
    <CustomModal
      title="Safety Measures"
      size="large"
      showModal
      handleClose={handleClose}
    >
      <CustomModal.Body className="xxs:p-4">
        <CustomModal.Title className="hidden lg:flex">
          Safety Measures
        </CustomModal.Title>
        <CustomModal.Content>
          <SafetyMeasuresContent />
        </CustomModal.Content>
      </CustomModal.Body>
    </CustomModal>
  );
}

export function SafetyMeasuresDesktop() {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FormValues>();

  const [
    isSafetyMeasuresOpen,
    { open: openSafetyMeasures, close: closeSafetyMeasures },
  ] = useToggle();

  const hasError = safetyMeasureConfigs.some((config) => {
    const errorEntry =
      errors?.safetyMeasures?.[
        config.key as keyof typeof errors.safetyMeasures
      ];
    return (
      typeof errorEntry === 'object' &&
      errorEntry !== null &&
      Object.keys(errorEntry).length > 0
    );
  });

  return (
    <div className="rounded xxs:bg-dex-black-800 xxs:p-2 xxs:space-y-1 lg:flex flex-col grow hidden">
      <div className="hidden lg:block font-bold text-sm xxs:text-dex-white-secondary">
        Safety Measures
      </div>

      <ActivteFiltersBar
        className={hasError ? 'xxs:border-red-500 hover:border-red-500' : ''}
      >
        <Button
          type="button"
          onClick={openSafetyMeasures}
          className="m-0 p-0 normal-case
"
        >
          <FilterTag label="+ Add/Change" />
        </Button>

        {safetyMeasureConfigs.map((config) => {
          const { key, label } = config;
          const rangeKey = `safetyMeasures.${key}Range`;
          const baseKey = `safetyMeasures.${key}`;

          const rangeActive = watch(rangeKey as any);

          if (!rangeActive) return null;

          return (
            <FilterTag
              key={key}
              label={label}
              onRemove={() => {
                // Disable the range and reset values via setValue
                setValue(rangeKey as any, false);
                setValue(`${baseKey}.min` as any, undefined);
                setValue(`${baseKey}.max` as any, undefined);
                if (config.hasPeriod) {
                  setValue(
                    `${baseKey}.period` as any,
                    HotTokensPeriods.TwentyFourHour
                  );
                }
              }}
            />
          );
        })}
      </ActivteFiltersBar>
      {isSafetyMeasuresOpen && (
        <SafetyMeasuresModal handleClose={closeSafetyMeasures} />
      )}
    </div>
  );
}

export function SafetyMeasuresMobile() {
  const {
    watch,
    formState: { errors },
  } = useFormContext<FormValues>();

  return (
    <div className="lg:hidden">
      <MobileExpandableCard
        mobileLabel="Safety Measures"
        trigger={
          <MobileExpandCardTrigger>
            {errors?.safetyMeasures ? (
              <div className="flex space-x-1 items-center text-sm xxs:text-red-500">
                <ExclamationCircleIcon className="xxs:w-6 h-6" />
                <div>Missing Information</div>
              </div>
            ) : (
              <div className="flex space-x-1 items-center text-sm xxs:text-dex-white-secondary">
                <div className="">Security:</div>

                {(() => {
                  const activeConfigs = safetyMeasureConfigs.filter((config) =>
                    watch(`safetyMeasures.${config.key}Range` as any)
                  );

                  const topThree = activeConfigs.slice(0, 3);
                  const extraCount = activeConfigs.length - topThree.length;

                  if (topThree.length === 0) {
                    return <div className="xxs:pl-1">Nothing selected...</div>;
                  }
                  return (
                    <>
                      {topThree.map((config) => {
                        const abbreviation = config.label
                          .replace(/ Range$/, '')
                          .split(' ')
                          .map((word) => word[0])
                          .join('')
                          .toUpperCase();

                        return (
                          <div
                            key={config.key}
                            className="flex space-x-1 items-center font-semibold"
                          >
                            <div>{abbreviation}</div>
                            <CheckCircleIcon className="w-5 h-5 xxs:text-dex-white" />
                          </div>
                        );
                      })}

                      {extraCount > 0 && (
                        <div className="font-semibold text-dex-white">
                          +{extraCount}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </MobileExpandCardTrigger>
        }
      >
        <SafetyMeasuresContent />
      </MobileExpandableCard>
    </div>
  );
}
